import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';

import type {
  AddComponentBody,
  ApproveBody,
  ConfigSaveBody,
  IncidentUpdateBody,
  ManualProposalBody,
  OpenIncidentBody,
  RedecideBody,
  RejectBody,
  ResolveIncidentBody,
  RetireBody,
  RetryBody,
  RevertBody,
  Scope,
} from '@/domain/types';
import { SCOPES } from '@/domain/types';

import { useApi, useApiContext } from './context';

/** The console has no push channel; views poll and every action refetches (TDD §5.2). */
export const POLL_MS = 30_000;

export const keys = {
  me: ['me'] as const,
  components: (scope: Scope) => ['components', scope] as const,
  queue: (scope: Scope) => ['queue', scope] as const,
  proposal: (id: string) => ['proposal', id] as const,
  runs: (scope: Scope, capabilityId: string) => ['runs', scope, capabilityId] as const,
  incidents: (scope: Scope) => ['incidents', scope] as const,
  config: (scope: Scope, capabilityId: string) => ['config', scope, capabilityId] as const,
  history: (scope: Scope, capabilityId: string) => ['config-history', scope, capabilityId] as const,
};

/**
 * Who the API takes the caller to be (CHG-050). Only a real API answers: the mock has no sign-in,
 * so it is never asked and the screens keep the typed name and every button.
 */
export function useMe() {
  const { api, mode } = useApiContext();
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api.me(),
    enabled: mode === 'http',
    staleTime: 60_000,
  });
}

export function useComponents(scope: Scope) {
  const api = useApi();
  return useQuery({
    queryKey: keys.components(scope),
    queryFn: () => api.getComponents(scope),
    refetchInterval: POLL_MS,
  });
}

export function useAllComponents() {
  const api = useApi();
  return useQueries({
    queries: SCOPES.map((scope) => ({
      queryKey: keys.components(scope),
      queryFn: () => api.getComponents(scope),
      refetchInterval: POLL_MS,
    })),
  });
}

export function useQueue(scope: Scope) {
  const api = useApi();
  return useQuery({
    queryKey: keys.queue(scope),
    queryFn: () => api.getQueue(scope),
    refetchInterval: POLL_MS,
  });
}

export function useAllQueues() {
  const api = useApi();
  return useQueries({
    queries: SCOPES.map((scope) => ({
      queryKey: keys.queue(scope),
      queryFn: () => api.getQueue(scope),
      refetchInterval: POLL_MS,
    })),
  });
}

export function useProposal(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.proposal(id),
    queryFn: () => api.getProposal(id),
    refetchInterval: POLL_MS,
  });
}

export function useRunHistory(scope: Scope, capabilityId: string) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: keys.runs(scope, capabilityId),
    queryFn: ({ pageParam }) =>
      api.getRuns(
        scope,
        capabilityId,
        pageParam ? { limit: 50, before: pageParam } : { limit: 50 },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_before,
  });
}

export function useIncidents(scope: Scope) {
  const api = useApi();
  return useQuery({
    queryKey: keys.incidents(scope),
    queryFn: () => api.getIncidents(scope),
    refetchInterval: POLL_MS,
  });
}

/**
 * Warm a scope's incidents before the operator lands on them: called when a link that leads
 * there is hovered or focused, so the switch shows data instead of a spinner. Components and
 * queues need no warming; the scope strip already keeps every scope's fresh.
 */
export function usePrefetchIncidents() {
  const api = useApi();
  const client = useQueryClient();
  return useCallback(
    (scope: Scope) => {
      void client.prefetchQuery({
        queryKey: keys.incidents(scope),
        queryFn: () => api.getIncidents(scope),
        staleTime: POLL_MS,
      });
    },
    [api, client],
  );
}

export function useConfig(scope: Scope, capabilityId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.config(scope, capabilityId),
    queryFn: () => api.getConfig(scope, capabilityId),
  });
}

export function useConfigHistory(scope: Scope, capabilityId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.history(scope, capabilityId),
    queryFn: () => api.getConfigHistory(scope, capabilityId),
  });
}

/** After any decision the queue, the component view and the proposal itself may all have moved. */
function useInvalidateScope() {
  const client = useQueryClient();
  return async (scope: Scope, proposalIds: string[] = []) => {
    await Promise.all([
      client.invalidateQueries({ queryKey: keys.components(scope) }),
      client.invalidateQueries({ queryKey: keys.queue(scope) }),
      client.invalidateQueries({ queryKey: keys.incidents(scope) }),
      ...proposalIds.map((id) => client.invalidateQueries({ queryKey: keys.proposal(id) })),
    ]);
  };
}

export function useApprove(scope: Scope) {
  const api = useApi();
  const invalidate = useInvalidateScope();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ApproveBody }) => api.approve(id, body),
    onSettled: (_data, _error, { id, body }) =>
      invalidate(scope, [id, ...(body.include ?? []).map((i) => i.id)]),
  });
}

export function useReject(scope: Scope) {
  const api = useApi();
  const invalidate = useInvalidateScope();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: RejectBody }) => api.reject(id, body),
    onSettled: (_data, _error, { id }) => invalidate(scope, [id]),
  });
}

export function useRedecide(scope: Scope) {
  const api = useApi();
  const invalidate = useInvalidateScope();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: RedecideBody }) => api.redecide(id, body),
    onSettled: (_data, _error, { id }) => invalidate(scope, [id]),
  });
}

export function useRetry(scope: Scope) {
  const api = useApi();
  const invalidate = useInvalidateScope();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: RetryBody }) => api.retry(id, body),
    onSettled: (_data, _error, { id }) => invalidate(scope, [id]),
  });
}

export function useManualProposal(scope: Scope) {
  const api = useApi();
  const invalidate = useInvalidateScope();
  return useMutation({
    mutationFn: (body: ManualProposalBody) => api.createManualProposal(body),
    onSettled: () => invalidate(scope),
  });
}

/** Incident actions (CHG-055): the page's list changes and a listed component's colour may show. */
export function useOpenIncident(scope: Scope) {
  const api = useApi();
  const invalidate = useInvalidateScope();
  return useMutation({
    mutationFn: (body: OpenIncidentBody) => api.openIncident(scope, body),
    onSettled: () => invalidate(scope),
  });
}

export function usePostIncidentUpdate(scope: Scope) {
  const api = useApi();
  const invalidate = useInvalidateScope();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: IncidentUpdateBody }) =>
      api.postIncidentUpdate(scope, id, body),
    onSettled: () => invalidate(scope),
  });
}

export function useResolveIncident(scope: Scope) {
  const api = useApi();
  const invalidate = useInvalidateScope();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ResolveIncidentBody }) =>
      api.resolveIncident(scope, id, body),
    onSettled: () => invalidate(scope),
  });
}

export function useSaveConfig(scope: Scope, capabilityId: string) {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: ConfigSaveBody) => api.saveConfig(scope, capabilityId, body),
    onSettled: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: keys.config(scope, capabilityId) }),
        client.invalidateQueries({ queryKey: keys.history(scope, capabilityId) }),
        client.invalidateQueries({ queryKey: keys.components(scope) }),
        client.invalidateQueries({ queryKey: keys.queue(scope) }),
      ]),
  });
}

export function useAddComponent(scope: Scope) {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: AddComponentBody) => api.addComponent(scope, body),
    onSettled: () => client.invalidateQueries({ queryKey: keys.components(scope) }),
  });
}

export function useRevertConfig(scope: Scope, capabilityId: string) {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: RevertBody) => api.revertConfig(scope, capabilityId, body),
    onSettled: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: keys.config(scope, capabilityId) }),
        client.invalidateQueries({ queryKey: keys.history(scope, capabilityId) }),
        client.invalidateQueries({ queryKey: keys.components(scope) }),
        client.invalidateQueries({ queryKey: keys.queue(scope) }),
      ]),
  });
}

/**
 * Retire a component (CHG-057). It is gone for good: its config, history and runs now answer 404,
 * so those queries are only marked stale (a refetch would be a 404 flashing on the page being
 * left), and every list that showed it is refreshed.
 */
export function useRetireComponent(scope: Scope, capabilityId: string) {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: RetireBody) => api.retireComponent(scope, capabilityId, body),
    onSettled: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: keys.config(scope, capabilityId),
          refetchType: 'none',
        }),
        client.invalidateQueries({
          queryKey: keys.history(scope, capabilityId),
          refetchType: 'none',
        }),
        client.invalidateQueries({ queryKey: keys.runs(scope, capabilityId), refetchType: 'none' }),
        client.invalidateQueries({ queryKey: keys.components(scope) }),
        client.invalidateQueries({ queryKey: keys.queue(scope) }),
        client.invalidateQueries({ queryKey: keys.incidents(scope) }),
      ]),
  });
}
